import React, { useState } from 'react';
import { Target, Award, Users, TrendingUp, ArrowRight, ChevronRight, Phone, Mail, MapPin, Building2, Wrench, Home, Truck, Palette, FileText, Shield, Settings, X, CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import VisionImage from "../assets/beng-civil-engineering.png";
import kwameImg from '../assets/kwame.png';
import amaImg from '../assets/ama.png';
import kofiImg from '../assets/kofi.png';
import abenaImg from '../assets/abena.png';

const AboutPage = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteStatus, setQuoteStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteStatus('sending');

    try {
      const response = await fetch('https://gusgroupltd.com/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: 'quote',
          website: '' // Honeypot field
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQuoteStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowQuoteModal(false);
          setQuoteStatus('');
        }, 3000);
      } else {
        setQuoteStatus('error');
        setTimeout(() => {
          setQuoteStatus('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error:', error);
      setQuoteStatus('error');
      setTimeout(() => {
        setQuoteStatus('');
      }, 5000);
    }
  };

const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Remodeling & Renovations",
      description: "Residential and commercial property upgrades."
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Road Construction & Civil Works",
      description: "Infrastructure and highway development."
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Real Estate Services",
      description: "Realtor activities, property management, and brokerage."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Furniture Supply & Design",
      description: "Custom and ready-made furniture solutions."
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Interior & Exterior Decoration",
      description: "Modern design, painting, and finishing services."
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Building Development & Construction",
      description: "Full-scale property development projects."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Importation & Exportation",
      description: "International trade facilitation."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Sales & Purchase of Properties",
      description: "Land, residential, and commercial property transactions."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Consultancy Services",
      description: "Advisory in real estate, construction, and business development."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Facility Management & Maintenance",
      description: "Long-term upkeep of buildings and infrastructure."
    }
  ];

  const team = [
      {
        name: "Kwame Asante",
        role: "Chief Executive Officer",
        image: kwameImg,
        bio: "20+ years in construction and real estate development"
      },
      {
        name: "Ama Osei",
        role: "Chief Operations Officer",
        image: amaImg,
        bio: "Expert in project management and client relations"
      },
      {
        name: "Kofi Mensah",
        role: "Chief Technical Officer",
        image: kofiImg,
        bio: "Specialized in civil engineering and infrastructure"
      },
      {
        name: "Abena Boateng",
        role: "Head of Design",
        image: abenaImg,
        bio: "Award-winning interior and exterior design specialist"
      }
    ];

  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Quality Excellence",
      description: "We never compromise on quality. Every project meets the highest international standards."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Integrity",
      description: "Transparency and honesty in all our dealings with clients, partners, and stakeholders."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Client-Centric",
      description: "Your vision is our mission. We prioritize client satisfaction in every project."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Innovation",
      description: "Embracing modern technology and sustainable practices in construction."
    }
  ];

  const milestones = [
    { year: "2009", title: "Company Founded", description: "GUS Group Ltd established in Accra" },
    { year: "2012", title: "First Major Project", description: "Completed 100-unit residential complex" },
    { year: "2015", title: "ISO Certification", description: "Achieved ISO 9001:2015 quality certification" },
    { year: "2018", title: "Regional Expansion", description: "Opened offices in Kumasi and Takoradi" },
    { year: "2021", title: "500th Project", description: "Celebrated completion of 500+ projects" },
    { year: "2024", title: "Excellence Award", description: "Ghana Construction Excellence Award winner" }
  ];

  const achievements = [
    "500+ Completed Projects",
    "200+ Satisfied Clients",
    "15+ Years of Excellence",
    "50+ Expert Team Members",
    "ISO 9001:2015 Certified",
    "Ghana Construction Award Winner"
  ];

  return (
    <div className="bg-black text-white">
      <Navigation onQuoteClick={() => setShowQuoteModal(true)} />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1920&h=1080&fit=crop')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
            About GUS Group
          </h2>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Building Ghana's
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Future Together
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            15+ years of excellence in construction, real estate, and comprehensive property solutions
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
                Our Story
              </h2>
              <h3 className="text-4xl md:text-5xl font-bold mb-6">
                From Vision to Ghana's Premier Construction Company
              </h3>
              <p className="text-gray-300 text-lg mb-6">
                Founded in 2009, GUS Group Ltd began with a simple mission: to deliver world-class construction 
                and real estate solutions that transform communities across Ghana.
              </p>
              <p className="text-gray-400 mb-6">
                What started as a small team of passionate builders has grown into one of Ghana's most trusted 
                construction companies. Over the past 15 years, we've completed over 500 projects ranging from 
                residential developments to major infrastructure works.
              </p>
              <p className="text-gray-400 mb-8">
                Today, we stand as a beacon of excellence in the construction industry, combining traditional 
                craftsmanship with modern innovation to deliver exceptional results that exceed client expectations.
              </p>
              <button 
                onClick={() => setShowQuoteModal(true)}
                className="bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center gap-2"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="relative">
              <img
                src={VisionImage}
                alt="Construction team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-black p-6 rounded-xl shadow-xl">
                <h4 className="text-4xl font-bold">15+</h4>
                <p className="font-semibold">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-gradient-to-br from-gray-900 to-black p-12 rounded-2xl border border-yellow-400/20">
              <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-yellow-400">Our Mission</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To deliver exceptional construction and real estate solutions that exceed client expectations, 
                while maintaining the highest standards of quality, safety, and sustainability. We are committed 
                to building lasting relationships and creating structures that stand the test of time.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-gray-900 to-black p-12 rounded-2xl border border-yellow-400/20">
              <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="text-3xl font-bold mb-4 text-yellow-400">Our Vision</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To be West Africa's most trusted and innovative construction company, recognized for excellence, 
                integrity, and transformative impact on communities. We envision a future where every project 
                contributes to Ghana's sustainable development and economic growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Values
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              What We Stand For
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our core values guide every decision we make and every project we undertake
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/10 hover:border-yellow-400/50 transition-all"
              >
                <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6">
                  <div className="text-yellow-400">
                    {value.icon}
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-3 text-white">
                  {value.title}
                </h4>
                <p className="text-gray-400">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Journey
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Milestones & Achievements
            </h3>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-yellow-400 via-yellow-500 to-yellow-600"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  className={`flex flex-col md:flex-row gap-8 items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all">
                      <h4 className="text-yellow-400 text-3xl font-bold mb-2">{milestone.year}</h4>
                      <h5 className="text-white text-xl font-bold mb-2">{milestone.title}</h5>
                      <p className="text-gray-400">{milestone.description}</p>
                    </div>
                  </div>

                  <div className="hidden md:flex w-16 h-16 bg-yellow-400 rounded-full items-center justify-center border-4 border-black z-10">
                    <CheckCircle className="w-8 h-8 text-black" />
                  </div>

                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Team
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Leadership That Inspires Excellence
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Meet the dedicated professionals driving GUS Group's success
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-yellow-400/10 hover:border-yellow-400/50 transition-all overflow-hidden"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-white mb-1">{member.name}</h4>
                  <p className="text-yellow-400 text-sm mb-3">{member.role}</p>
                  <p className="text-gray-400 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Achievements
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Proven Track Record
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all flex items-center gap-4"
              >
                <CheckCircle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                <span className="text-white text-lg font-semibold">{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(0,0,0,.1) 35px, rgba(0,0,0,.1) 70px)'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied clients who trust GUS Group for their construction needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowQuoteModal(true)}
              className="bg-black text-yellow-400 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-900 transition-all transform hover:scale-105 flex items-center gap-2 justify-center"
            >
              Get Free Quote
              <ArrowRight className="w-5 h-5" />
            </button>
            <a 
              href="tel:0592668258"
              className="border-2 border-black text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-black hover:text-yellow-400 transition-all"
            >
              Call: 0592668258
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-white">Request a Quote</h3>
                <button
                  onClick={() => {
                    setShowQuoteModal(false);
                    setQuoteStatus('');
                  }}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all"
                  disabled={quoteStatus === 'sending'}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Status Messages */}
              {quoteStatus === 'sending' && (
                <div className="mb-6 bg-blue-500/10 border border-blue-500/50 rounded-xl p-4 flex items-start gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <div>
                    <h4 className="text-blue-500 font-bold mb-1">Sending Request...</h4>
                    <p className="text-gray-300 text-sm">Please wait while we process your quote request.</p>
                  </div>
                </div>
              )}

              {quoteStatus === 'success' && (
                <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-green-500 font-bold mb-1">Quote Request Sent Successfully!</h4>
                    <p className="text-gray-300 text-sm">Thank you! We'll contact you soon with your quote. This window will close automatically.</p>
                  </div>
                </div>
              )}

              {quoteStatus === 'error' && (
                <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                  <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-red-500 font-bold mb-1">Failed to Send Request</h4>
                    <p className="text-gray-300 text-sm">Please try again or call us directly at 0592668258</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={quoteStatus === 'sending'}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={quoteStatus === 'sending'}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="0501234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Service Required *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, i) => (
                      <option key={i} value={service.title}>{service.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Project Details *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    rows="5"
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tell us about your project requirements, timeline, budget, etc."
                  ></textarea>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuoteModal(false);
                      setQuoteStatus('');
                    }}
                    disabled={quoteStatus === 'sending'}
                    className="flex-1 border-2 border-gray-700 text-white px-8 py-4 rounded-full font-bold hover:border-yellow-400 hover:text-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={quoteStatus === 'sending' || quoteStatus === 'success'}
                    className="flex-1 bg-yellow-400 text-black px-8 py-4 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {quoteStatus === 'sending' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Floating Button */}
      
      <a  href="https://wa.me/233592668258?text=Hello%20GUS%20Group,%20I'm%20interested%20in%20your%20services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 z-40"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default AboutPage;